import React, { useState } from "react";
import {
  Container,
  AppBar,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { Link, useParams } from "react-router-dom";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import useSWR from "swr";

export default function DeleteAccount() {
  const params = useParams();
  const [mobNoInput, setMobNoInput] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data: resAbout } = useSWR(`/saral/about?mobileNo=${params.MOBNOCO}`);

  const compName = resAbout?.data?.result?.[0]?.COMP_NAME || "";

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div>
        <AppBar elevation={1} color="default" position="static">
          <div className="container py-2">
            <Typography variant="h6" color="textPrimary">
              Account Deletion
            </Typography>
          </div>
        </AppBar>
        <Container maxWidth="sm">
          <div style={{ paddingTop: "40px", paddingBottom: "40px" }}>
            <Paper
              elevation={2}
              style={{ padding: "40px", textAlign: "center" }}
            >
              <CheckCircleOutlineIcon
                style={{
                  fontSize: "64px",
                  color: "#4caf50",
                  marginBottom: "16px",
                }}
              />
              <Typography
                variant="h5"
                style={{ marginBottom: "12px", fontWeight: 600 }}
              >
                {compName && <strong>{compName}</strong>} - Deletion Request
                Submitted
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ marginBottom: "16px" }}
              >
                Your request to delete your{" "}
                {compName && <strong>{compName}</strong>} account has been
                received.
              </Typography>
              <Paper
                variant="outlined"
                style={{
                  padding: "16px",
                  marginBottom: "16px",
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, marginBottom: "8px" }}
                >
                  What happens next:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: "6px" }}
                >
                  • Your account and all associated personal data will be
                  permanently deleted within <strong>5 business days</strong>.
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: "6px" }}
                >
                  • Certain data may be retained for a limited period where
                  required by law or for fraud prevention purposes.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • You will not be able to recover your account or data once
                  the deletion is complete.
                </Typography>
              </Paper>
              <Typography variant="body2" color="textSecondary">
                If you submitted this request by mistake, please contact our
                support team immediately to cancel it before processing begins.
              </Typography>
            </Paper>
          </div>
        </Container>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div>
        <AppBar elevation={1} color="default" position="static">
          <div className="container py-2">
            <Button
              style={{ textTransform: "none", padding: 0 }}
              onClick={() => setConfirmed(false)}
            >
              <Typography variant="h6" color="textPrimary">
                ← Back
              </Typography>
            </Button>
          </div>
        </AppBar>
        <Container maxWidth="sm">
          <div style={{ paddingTop: "32px", paddingBottom: "40px" }}>
            <Paper elevation={2} style={{ padding: "32px" }}>
              <Typography
                variant="h6"
                style={{ fontWeight: 600, marginBottom: "16px" }}
              >
                Confirm Account Deletion
              </Typography>

              <Typography
                variant="body2"
                color="error"
                style={{ marginBottom: "20px" }}
              >
                This is your final confirmation. Once submitted, your account
                and all data will be permanently deleted and cannot be
                recovered.
              </Typography>

              <TextField
                fullWidth
                label="Registered Mobile Number"
                variant="outlined"
                value={mobNoInput}
                onChange={(e) => setMobNoInput(e.target.value)}
                style={{ marginBottom: "24px" }}
                inputProps={{ maxLength: 15 }}
                helperText="Enter the mobile number linked to your account to confirm"
              />

              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSubmit}
                  style={{ flex: 1 }}
                  disabled={!mobNoInput.trim()}
                >
                  Confirm Delete
                </Button>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => setConfirmed(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </div>
            </Paper>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <AppBar elevation={1} color="default" position="static">
        <div className="container py-2">
          <Link to={`/${params.MOBNOCO}`} style={{ textDecoration: "none" }}>
            <Typography variant="h6" color="textPrimary">
              ← Back
            </Typography>
          </Link>
        </div>
      </AppBar>

      <Container maxWidth="sm">
        <div style={{ paddingTop: "32px", paddingBottom: "40px" }}>
          <Paper elevation={2} style={{ padding: "32px" }}>
            <Typography
              variant="h5"
              style={{ fontWeight: 600, marginBottom: "8px" }}
            >
              Delete Account
            </Typography>
            {compName ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: "20px" }}
              >
                You are requesting to delete your <strong>{compName}</strong>{" "}
                account.
              </Typography>
            ) : null}

            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: "16px" }}
            >
              Developer: Vijay J Prajapati
            </Typography>

            <Divider style={{ marginBottom: "20px" }} />

            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, marginBottom: "8px" }}
            >
              What will be permanently deleted:
            </Typography>
            <List dense disablePadding style={{ marginBottom: "20px" }}>
              {[
                "Your account profile and personal information",
                "Order history and transaction records",
                "Saved addresses and contact details",
                "App preferences and settings",
                "All other data associated with your account",
              ].map((item) => (
                <ListItem
                  key={item}
                  disableGutters
                  style={{ paddingTop: "2px", paddingBottom: "2px" }}
                >
                  <ListItemText
                    primary={`• ${item}`}
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "textSecondary",
                    }}
                  />
                </ListItem>
              ))}
            </List>

            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, marginBottom: "8px" }}
            >
              Data retention after deletion:
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: "20px" }}
            >
              Certain data may be retained for a limited period where required
              by applicable law or for legitimate purposes such as fraud
              prevention and security. All other data is permanently deleted
              within <strong>5 business days</strong> of your request.
            </Typography>

            <Paper
              variant="outlined"
              style={{
                padding: "12px 16px",
                marginBottom: "24px",
                backgroundColor: "#fff8e1",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                <strong>Note:</strong> This action is permanent and cannot be
                undone. You will lose access to your account immediately after
                deletion is processed.
              </Typography>
            </Paper>

            <div style={{ display: "flex", gap: "16px" }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setConfirmed(true)}
                style={{ flex: 1 }}
              >
                Delete My Account
              </Button>
              <Button
                variant="outlined"
                color="default"
                component={Link}
                to={`/${params.MOBNOCO}`}
                style={{ flex: 1 }}
              >
                Keep Account
              </Button>
            </div>
          </Paper>
        </div>
      </Container>
    </div>
  );
}
